import { useMemo, useState } from "react";
import { MERCARI_CATEGORY_PATHS } from "@/lib/mercariCategories";
import { mercariCategoryStack, mercariCategoryTree } from "@/lib/mercariCategoryTree";

function descendants(node, out = []) {
  for (const child of node.children) {
    out.push(child);
    descendants(child, out);
  }
  return out;
}

export default function MercariCategoryField({ value, onChange, className, paths }) {
  const tree = useMemo(() => mercariCategoryTree(paths || MERCARI_CATEGORY_PATHS), [paths]);
  const [open, setOpen] = useState(false);
  const [stack, setStack] = useState([]);
  const [query, setQuery] = useState("");
  const current = stack.length ? stack[stack.length - 1] : tree;
  const q = query.trim().toLowerCase();
  const hits = q
    ? descendants(current).filter((node) => node.path.toLowerCase().includes(q)).slice(0, 100)
    : [];

  function show(next) {
    setQuery("");
    setStack(next ? mercariCategoryStack(tree, next) : []);
    setOpen(true);
  }

  function choose(path) {
    onChange(path);
    setOpen(false);
    setQuery("");
  }

  function drill(node) {
    setQuery("");
    setStack(mercariCategoryStack(tree, node.path));
  }

  return (
    <div className="block text-sm">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : show(value))}
        className={`${className || ""} mt-1 text-left`}
      >
        {value || "Select a category"}
      </button>
      {open ? (
        <div className="mt-1 rounded-lg border border-aura-border bg-aura-surface">
          <div className="flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-wide text-aura-muted">
            <span>Category Selection</span>
            <button type="button" className="normal-case tracking-normal text-sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1 px-3 pb-2 text-sm">
            <button type="button" className={stack.length ? "text-aura-muted" : "font-medium"} onClick={() => { setStack([]); setQuery(""); }}>
              All
            </button>
            {stack.map((node, index) => (
              <span key={node.path} className="flex items-center gap-1">
                <span className="text-aura-muted">&gt;</span>
                <button
                  type="button"
                  className={index === stack.length - 1 ? "font-medium" : "text-aura-muted"}
                  onClick={() => { setStack(stack.slice(0, index + 1)); setQuery(""); }}
                >
                  {node.name}
                </button>
              </span>
            ))}
          </div>
          <div className="px-3 pb-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={current.path ? `Search under "${current.name}" category children` : "Search on all categories"}
              className={`${className || ""} text-sm`}
            />
          </div>
          <ul className="max-h-60 overflow-auto border-t border-aura-border">
            {(q ? hits : current.children).map((node) => {
              const selected = value === node.path;
              return (
                <li key={node.path}>
                  <button
                    type="button"
                    onClick={() => (node.children.length ? drill(node) : choose(node.path))}
                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-aura-accent/10 ${selected ? "bg-aura-accent/15" : ""}`}
                  >
                    {q ? node.path : node.name}
                  </button>
                </li>
              );
            })}
            {!q && current.path ? (
              <li>
                <button
                  type="button"
                  onClick={() => choose(current.path)}
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-aura-accent/10 ${value === current.path ? "bg-aura-accent/15" : ""}`}
                >
                  All {current.name}
                </button>
              </li>
            ) : null}
            {q && !hits.length ? (
              <li className="px-3 py-2 text-sm text-aura-muted">No categories</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
